import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { categoryMap } from "@/mocks/articles";

const catBgClasses: Record<string, string> = {
  accent: "bg-accent-500",
  primary: "bg-primary-500",
  secondary: "bg-secondary-500",
};

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const cat = categoryMap[article.category];

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - 51黑料网`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
            <span>{article.publishedAt}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </span>
              {article.viewCount > 10000
                ? `${(article.viewCount / 10000).toFixed(1)}万`
                : article.viewCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300"
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - 51黑料网`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 text-xs font-medium rounded-full text-background-50 ${catBgClasses[cat.color]}`}>
              {cat.name}
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-lg font-heading font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-500 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between mt-4 text-xs text-foreground-400">
            <span>{article.publishedAt}</span>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.viewCount > 10000
                  ? `${(article.viewCount / 10000).toFixed(1)}万`
                  : article.viewCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-heart-line"></i>
                </span>
                {article.likeCount > 10000
                  ? `${(article.likeCount / 10000).toFixed(1)}万`
                  : article.likeCount}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          title={`${article.title} - 51黑料网`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full text-background-50 ${catBgClasses[cat.color]}`}>
            {cat.name}
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-base font-heading font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-500 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between mt-4 text-xs text-foreground-400">
          <span>{article.publishedAt}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </span>
              {article.viewCount > 10000
                ? `${(article.viewCount / 10000).toFixed(1)}万`
                : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-heart-line"></i>
              </span>
              {article.likeCount > 10000
                ? `${(article.likeCount / 10000).toFixed(1)}万`
                : article.likeCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}